import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sparkles, Send, FileCode2, Check, AlertCircle, RefreshCw, Activity } from 'lucide-react';
import uapiIntegrationsService from '../services/UapiIntegrationsService';
import marketplaceExtrasService from '../services/marketplaceExtrasService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import { formatDateTime } from '../utils/datetime';

// The integration assistant — the model that writes and repairs this integration's code (§7 of
// UAPI-INTEGRATIONS.md).
//
// ITS OWN COMPONENT, not a section of the detail page, for the reason any panel earns one: it holds a
// conversation, an evidence selection and a set of proposed files, none of which the page around it
// needs to know about. The page hands it the integration and a callback for "a file was written".
//
// EVIDENCE FIRST. The picker at the top lists recent REQUESTS on this system, not log lines — pick the
// one that failed and the server hands the model that transaction's whole trace: what the Universal API
// resolved, what the integration sent, what the backend answered. Asking without picking is answered
// from the most recent traffic instead, which is right for "why is everything 500ing" and wrong for one
// reservation.
//
// NOTHING IS WRITTEN UNTIL SOMEBODY PRESSES APPLY. The reply carries whole files; each is shown with
// its path and an Apply that goes through the ordinary save — into the DRAFT, with Check and Publish
// unchanged behind it. The model never touches the tree itself, so no answer of its can reach a
// property without a person having read it and a version having been published.

// The reply, split into prose and fenced blocks. A block tagged `path=` is a file the model wants
// written and gets its own card; anything else is illustration and stays inline as code.
const FENCE = /```([a-zA-Z0-9]*)(?:\s+path=([^\s`]+))?\s*\n([\s\S]*?)```/g;

// The same path rule the file route enforces. A block naming '../x' or an absolute path is refused by
// the server, so offering an Apply button for one would only find that out after the click — the card
// shows the code and no button instead.
// The reserved path the model uses for the maintained document. It is NOT a file in the integration
// tree: it goes to the marketplace's API Doc tab, so it survives publishing, restoring or deleting a
// version and can be read without opening this editor. Must match API_DOC_PATH in assistant.js.
export const API_DOC_PATH = '@api-doc';

export const isApplyablePath = (path) => {
    if (path === API_DOC_PATH) return false;   // applied through its own button, not the file route
    const p = String(path || '');
    if (!p || p.length > 512) return false;
    if (p.startsWith('/') || p.includes('\\')) return false;
    return !p.split('/').some(seg => seg === '..' || seg === '.' || seg === '');
};

export const splitReply = (reply) => {
    const parts = [];
    let last = 0;
    let m;
    FENCE.lastIndex = 0;
    while ((m = FENCE.exec(reply || ''))) {
        if (m.index > last) parts.push({ kind: 'text', text: reply.slice(last, m.index) });
        parts.push({ kind: 'code', path: (m[2] || '').replace(/^['"]|['"]$/g, '') || null, code: m[3] });
        last = m.index + m[0].length;
    }
    if (last < (reply || '').length) parts.push({ kind: 'text', text: reply.slice(last) });
    return parts.filter(p => p.kind !== 'text' || p.text.trim());
};

function UapiIntegrationAssistant({ integrationId, marketplaceId, readOnly, onApplied }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [selected, setSelected] = useState('');
    const [loadingTraces, setLoadingTraces] = useState(false);
    const [applied, setApplied] = useState({});     // path -> true, so a card says so after it is written
    const endOfThread = useRef(null);

    const loadTraces = useCallback(async () => {
        try {
            setLoadingTraces(true);
            const res = await uapiIntegrationsService.traces(integrationId);
            const d = res?.data || res || {};
            setTransactions(Array.isArray(d.transactions) ? d.transactions : []);
        } catch (err) {
            // A missing trace is not a reason to fail the panel: the assistant works without one, and
            // the 3-day index is empty far more often than it is broken.
            setTransactions([]);
        } finally {
            setLoadingTraces(false);
        }
    }, [integrationId]);

    useEffect(() => { loadTraces(); }, [loadTraces]);
    useEffect(() => { endOfThread.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages, busy]);

    const ask = async () => {
        const text = input.trim();
        if (!text || busy) return;
        const next = [...messages, { role: 'user', content: text }];
        setMessages(next);
        setInput('');
        setError(null);
        setBusy(true);
        try {
            const tx = transactions.find(t => t.transaction_id === selected);
            const res = await uapiIntegrationsService.assist(integrationId, next, selected
                ? { transaction_id: selected, full_path: tx?.operation || null }
                : null);
            const d = res?.data || res || {};
            setMessages([...next, {
                role: 'assistant',
                content: d.reply || '(no response)',
                files: Array.isArray(d.files) ? d.files : [],
                apiDoc: d.apiDoc || null,
                truncated: !!d.truncated,
            }]);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'The assistant could not be reached');
            // The question stays in the thread. Removing it would lose what was typed, and the next
            // attempt is nearly always the same question.
        } finally {
            setBusy(false);
        }
    };

    const apply = async (file) => {
        try {
            setBusy(true);
            setError(null);
            await uapiIntegrationsService.saveFile(integrationId, file.path, file.content);
            setApplied(a => ({ ...a, [file.path]: true }));
            if (onApplied) await onApplied(file.path);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || `${file.path} could not be written`);
        } finally {
            setBusy(false);
        }
    };

    // Write the maintained document back to the marketplace's API Doc tab. Separate from apply()
    // because it is a different destination entirely — the integration's file route would put it in
    // the draft tree, where the tab cannot see it and every published version would carry a copy.
    const applyDoc = async (doc) => {
        if (!doc || !marketplaceId) return;
        try {
            setBusy(true);
            setError(null);
            const payload = { label: doc.label, doc_type: 'markdown', location: '', content: doc.content };
            if (doc.id) await marketplaceExtrasService.apiDocs.update(marketplaceId, doc.id, payload);
            else await marketplaceExtrasService.apiDocs.create(marketplaceId, payload);
            setApplied(a => ({ ...a, [API_DOC_PATH]: true }));
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'The notes could not be saved');
        } finally {
            setBusy(false);
        }
    };

    // Enter sends, Shift+Enter is a newline — the convention in every chat box, and the alternative
    // (a button only) makes a back-and-forth conversation feel like filling in a form.
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); }
    };

    return (
        <div className="uai-card">
            <div className="uai-card-head">
                <h2 className="uai-card-title"><Sparkles size={15} /> Assistant</h2>
                <span className="uai-hint">
                    Writes and repairs this integration from the gateway trace. It proposes whole files;
                    nothing is written until you apply one.
                </span>
            </div>

            {/* Evidence. A request, not a log line — picking one hands the model that transaction's
                whole trace, which is the difference between repairing the code and guessing at it. */}
            <div className="uai-asst-evidence">
                <span className="uai-label-inline"><Activity size={13} /> About</span>
                <select
                    className="uai-input uai-input-inline uai-asst-txselect"
                    value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                >
                    <option value="">Recent traffic on this system</option>
                    {transactions.map(t => (
                        <option key={t.transaction_id} value={t.transaction_id}>
                            {[
                                formatDateTime(t.at),
                                `${t.method || ''} ${t.operation || t.path || ''}`.trim(),
                                t.status != null ? `→ ${t.status}` : t.outcome,
                                t.integration_version != null ? `v${t.integration_version}` : null,
                            ].filter(Boolean).join('  ·  ')}
                        </option>
                    ))}
                </select>
                <button className="uai-icon-btn" title="Reload recent requests" onClick={loadTraces} disabled={loadingTraces}>
                    <RefreshCw size={14} />
                </button>
                {!loadingTraces && transactions.length === 0 && (
                    <span className="uai-hint uai-hint-inline">
                        Nothing traced in the last 3 days — the assistant will work from the code alone.
                    </span>
                )}
            </div>

            {error && <div className="uai-banner uai-banner-error uai-asst-banner"><AlertCircle size={16} /> <span>{error}</span></div>}

            <div className="uai-asst-thread">
                {messages.length === 0 && (
                    <p className="uai-empty-inline">
                        Ask it to implement an operation, or pick a failed request above and ask why it failed.
                        It already has the file tree, the operations this system is granted, the credential
                        setting names, and the trace.
                    </p>
                )}

                {messages.map((m, i) => (
                    <div key={i} className={`uai-msg uai-msg-${m.role}`}>
                        {m.role === 'user'
                            ? <p className="uai-msg-text">{m.content}</p>
                            : splitReply(m.content).map((part, j) => (part.kind === 'text'
                                ? <p key={j} className="uai-msg-text">{part.text.trim()}</p>
                                : (
                                    <div key={j} className="uai-asst-file">
                                        <div className="uai-asst-file-head">
                                            <FileCode2 size={13} />
                                            <code>{part.path || 'example'}</code>
                                            {/* The document goes to the marketplace's API Doc tab, not
                                                into the draft — so it gets its own button, and its own
                                                sentence saying where it lands. */}
                                            {part.path === API_DOC_PATH && (applied[API_DOC_PATH]
                                                ? <span className="uai-asst-applied"><Check size={13} /> saved to the API Doc tab</span>
                                                : (
                                                    <Button
                                                        variant="secondary"
                                                        disabled={busy || readOnly || !m.apiDoc}
                                                        onClick={() => applyDoc({ ...(m.apiDoc || {}), content: part.code })}
                                                    >
                                                        Save to integration notes
                                                    </Button>
                                                ))}
                                            {part.path && part.path !== API_DOC_PATH && !isApplyablePath(part.path) && (
                                                <span className="uai-hint uai-hint-inline">
                                                    not a path inside this integration
                                                </span>
                                            )}
                                            {part.path && isApplyablePath(part.path) && (applied[part.path]
                                                ? <span className="uai-asst-applied"><Check size={13} /> written to the draft</span>
                                                : (
                                                    <Button
                                                        variant="secondary"
                                                        disabled={busy || readOnly}
                                                        onClick={() => apply({ path: part.path, content: part.code })}
                                                    >
                                                        Apply to draft
                                                    </Button>
                                                ))}
                                        </div>
                                        <pre className="uai-asst-code">{part.code}</pre>
                                    </div>
                                )))}
                        {m.truncated && (
                            <p className="uai-hint">
                                The answer was cut off at the token limit, so no file is offered — applying half a
                                file would delete the rest of it. Ask for one file at a time.
                            </p>
                        )}
                    </div>
                ))}

                {busy && <div className="uai-loading"><Spinner /> Thinking…</div>}
                <div ref={endOfThread} />
            </div>

            {readOnly && (
                <p className="uai-hint">
                    You are viewing a published version. The assistant still answers, but a file can only be
                    applied to the draft — switch to it above first.
                </p>
            )}

            <div className="uai-asst-composer">
                <textarea
                    className="uai-input uai-asst-input"
                    rows={3}
                    value={input}
                    placeholder={'e.g. "implement GET /hotels/{hotelId}/reservations" or "this 403 — what is wrong with our request?"'}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                />
                <Button onClick={ask} disabled={busy || !input.trim()}>
                    <Send size={14} /> Ask
                </Button>
            </div>
        </div>
    );
}

export default UapiIntegrationAssistant;
